import React, { useState, useEffect, useContext } from "react";
import "./Distribution.css";
import Url from '../Url';
import { LoginContext } from '../Login/LoginContext';
import { FaFilter, FaBuilding, FaUser } from "react-icons/fa";

export default function Distribution() {
  const { loginResult } = useContext(LoginContext);

  // State for view mode: 'dragAndDrop' or 'assign'
  const [viewMode, setViewMode] = useState('dragAndDrop'); // Default to drag and drop
  const [showFilterDropdown, setShowFilterDropdown] = useState(false); // State to control filter dropdown visibility

  // --- Common States for both views ---
  const [employees, setEmployees] = useState([]);
  const user_id = loginResult.user_id;
  const report_id = 6; // Report ID for fetching leads
  const save_report_id = 7; // Report ID for saving assignments

  // --- Drag and Drop Specific States ---
  const [selectedEmployeeDragDrop, setSelectedEmployeeDragDrop] = useState(null);
  const [leadsDragDrop, setLeadsDragDrop] = useState([]);
  const [assignedLeadsDragDrop, setAssignedLeadsDragDrop] = useState([]);
  const [draggedLead, setDraggedLead] = useState(null);

  // --- Assign Specific States ---
  const [selectedEmployeeAssign, setSelectedEmployeeAssign] = useState("");
  const [leadsAssign, setLeadsAssign] = useState([]);
  // --- Popup States (Unified for both views) ---
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessageData, setPopupMessageData] = useState(null); // Stores the API response or custom message object
  const [popupMessageType, setPopupMessageType] = useState('success'); // 'success' or 'error'

  const [firstSelectedIndex, setFirstSelectedIndex] = useState(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [selectedLeadIndex, setSelectedLeadIndex] = useState(null);

  // --- Fetch Employees (Common) ---
  const fetchEmployees = () => {
    if (!user_id) return; // Ensure user_id exists before fetching

    fetch(
      `${Url.Url}/get_report?data=` +
      encodeURIComponent(
        JSON.stringify({
          report_id: 5,
          user_id: user_id,
        })
      )
    )
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && Array.isArray(data)) {
          setEmployees(data);
        } else {
          setEmployees([]); // Ensure employees is an array even if data is not as expected
        }
      })
      .catch((err) => {
        console.error("API Error (Employees):", err);
        setPopupMessageType('error');
        setPopupMessageData([{ 0: { message: "Error fetching employees." } }]);
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      });
  };

  // --- Fetch Leads (Common, adapted for both) ---
  const fetchLeads = async (currentViewMode) => {
    if (!user_id) return; // Ensure user_id exists before fetching

    try {
      const response = await fetch(
        `${Url.Url}/get_report?data={"user_id":"${user_id}","report_id":"${report_id}"}`
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with status ${response.status}: ${errorText.substring(0, 100)}...`);
      }

      const result = await response.json();
      console.log("Raw Leads API Response:", result); // Log the raw response to inspect lead_id

      if (Array.isArray(result) && result.length > 0) {
        const leadsRaw = result;
        const leadsParsed = leadsRaw.map((lead) => {
          let parsedDetails = {};
          try {
            parsedDetails = JSON.parse(lead.Details || '{}');
          } catch (e) {
            console.warn('Failed to parse Details for lead:', lead.lead_id, e);
          }
          // Use 'lead_id' directly from the backend response as the unique identifier
          return { ...lead, id: lead.lead_id, parsedDetails };
        });

        if (currentViewMode === 'dragAndDrop') {
          setLeadsDragDrop(leadsParsed);
        } else if (currentViewMode === 'assign') {
          setLeadsAssign(leadsParsed);
        }
      } else {
        if (currentViewMode === 'dragAndDrop') {
          setLeadsDragDrop([]);
        } else if (currentViewMode === 'assign') {
          setLeadsAssign([]);
        }
      }
    } catch (error) {
      setPopupMessageType('error');
      setPopupMessageData([{ 0: { message: 'Error fetching leads: ' + error.message } }]);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      console.error("Fetch Leads Error:", error);

      if (currentViewMode === 'dragAndDrop') {
        setLeadsDragDrop([]);
      } else if (currentViewMode === 'assign') {
        setLeadsAssign([]);
      }
    }
  };

  // --- Load employees & leads on mount (common) ---
  useEffect(() => {
    fetchEmployees();
    fetchLeads(viewMode); // Initial fetch based on default viewMode
  }, [user_id, viewMode]);

  // --- Drag and Drop Handlers ---
  useEffect(() => {
    // Clear assigned leads when employee selection changes in drag and drop
    if (selectedEmployeeDragDrop) {
      setAssignedLeadsDragDrop([]);
    }
  }, [selectedEmployeeDragDrop]);

  const onDragStart = (lead) => {
    setDraggedLead(lead);
  };

  const onDropAssigned = (e) => {
    e.preventDefault();
    if (!draggedLead) return;

    if (!assignedLeadsDragDrop.find(l => l.id === draggedLead.id)) {
      setAssignedLeadsDragDrop([...assignedLeadsDragDrop, draggedLead]);
      setLeadsDragDrop(leadsDragDrop.filter(l => l.id !== draggedLead.id));
    }
    setDraggedLead(null);
  };

  const onDropLeads = (e) => {
    e.preventDefault();
    if (!draggedLead) return;

    if (!leadsDragDrop.find(l => l.id === draggedLead.id)) {
      setLeadsDragDrop([...leadsDragDrop, draggedLead]);
      setAssignedLeadsDragDrop(assignedLeadsDragDrop.filter(l => l.id !== draggedLead.id));
    }
    setDraggedLead(null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  const handleEmployeeChangeDragDrop = (e) => {
    const empId = e.target.value;
    const emp = employees.find(emp => emp.user_id?.toString() === empId); // Use optional chaining
    setSelectedEmployeeDragDrop(emp || null);
  };

  // --- Unified Save Assignments Function ---
  const saveAssignments = (employeeId, leadIds, currentViewMode) => {
    if (!employeeId) {
      setPopupMessageType('error');
      setPopupMessageData([{ 0: { message: "Please select an employee first." } }]);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2000);
      return;
    }
    if (leadIds.length === 0) {
      setPopupMessageType('error');
      setPopupMessageData([{ 0: { message: "No leads selected to save." } }]);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2000);
      return;
    }

    const payload = {
      user_id,
      employee_id: employeeId,
      lead_ids: leadIds,
      report_id: save_report_id
    };
    console.log("Sending payload:", JSON.stringify(payload));

    const queryString = encodeURIComponent(JSON.stringify(payload));
    const apiUrl = `${Url.Url}/get_report?data=${queryString}`;

    fetch(apiUrl)
      .then(res => res.json())
      .then(data => {
        setPopupMessageType('success');
        setPopupMessageData(data); // Assuming 'data' contains the success message
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
          setPopupMessageData(null); // Clear message data
          // Clear and refresh based on the view mode
          if (currentViewMode === 'dragAndDrop') {
            setAssignedLeadsDragDrop([]);
            fetchLeads('dragAndDrop'); // Refresh leads for drag and drop view
          } else if (currentViewMode === 'assign') {
            fetchLeads('assign'); // Refresh leads for assign view
            setFirstSelectedIndex(null);
            setLastSelectedIndex(null);
            setSelectedLeadIndex(null);
          }
        }, 2000); // Show success popup for 2 seconds
      })
      .catch(err => {
        setPopupMessageType('error');
        setPopupMessageData([{ 0: { message: "Error saving assignments: " + err.message } }]);
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      });
  };

  const handleSaveAssignmentsDragDrop = () => {
    const leadIds = assignedLeadsDragDrop.map(lead => lead.id);
    saveAssignments(selectedEmployeeDragDrop.user_id, leadIds, 'dragAndDrop');
  };

  const renderLeadDetails = (lead) => {
    const fieldsToDisplay = [
      "Company Name",
      "Contact Person",
      "Lead Source",
      "Lead Date",
      "Address",
      "Mobile",
      "Status",
    ];

    const getFieldValue = (field) =>
      lead.parsedDetails?.[field] ?? lead[field] ?? "N/A";

    return fieldsToDisplay.map((field) => (
      <div key={field} className="lead-detail-item">
        <span className="lead-detail-label">{field}:</span>{" "}
        <span className="lead-detail-value">{getFieldValue(field)}</span>
      </div>
    ));
  };


  // --- Assign Specific Handlers ---
  useEffect(() => {
    // Auto-refresh leads for "Assign" mode if active
    if (viewMode === 'assign') {
      const interval = setInterval(() => {
        fetchLeads('assign');
      }, 30000); // Auto-refresh every 30 seconds
      return () => clearInterval(interval); // Cleanup on unmount
    }
  }, [user_id, report_id, viewMode]);


  const handleEmployeeChangeAssign = (e) => {
    setSelectedEmployeeAssign(e.target.value);
    setFirstSelectedIndex(null);
    setLastSelectedIndex(null);
    setSelectedLeadIndex(null);
  };

  const handleTileClick = (clickedIndex) => {
    if (firstSelectedIndex === null) {
      setFirstSelectedIndex(clickedIndex);
      setLastSelectedIndex(clickedIndex);
    } else {
      setLastSelectedIndex(clickedIndex);
    }
    setSelectedLeadIndex(clickedIndex);
  };

  const handleUpdateAssign = () => {
    if (
      firstSelectedIndex === null ||
      lastSelectedIndex === null
    ) {
      setPopupMessageType('error');
      setPopupMessageData([{ 0: { message: "Please select leads to assign." } }]);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 2000);
      return;
    }

    const start = Math.min(firstSelectedIndex, lastSelectedIndex);
    const end = Math.max(firstSelectedIndex, lastSelectedIndex);

    const selectedLeads = leadsAssign.slice(start, end + 1);
    const selectedLeadIds = selectedLeads.map((lead) => lead.id);

    saveAssignments(selectedEmployeeAssign, selectedLeadIds, 'assign');
  };

  return (
    <div className="distribution-container">
      <h2 className="main-title">New Enquiry Leads</h2>

      <div className="filter-section">
        <div className="filter-icon-container">
          <FaFilter className="filter-icon" onClick={() => setShowFilterDropdown(!showFilterDropdown)} />
          {showFilterDropdown && (
            <div className="filter-dropdown">
              <div className="dropdown-item" onClick={() => { setViewMode('dragAndDrop'); setShowFilterDropdown(false); }}>Drag and Drop</div>
              <div className="dropdown-item" onClick={() => { setViewMode('assign'); setShowFilterDropdown(false); }}>Assign</div>
            </div>
          )}
        </div>
      </div>

      {/* Unified Popup Message */}
      {showPopup && popupMessageData && (
        <div className={`popup-message ${popupMessageType} ${showPopup ? 'show' : ''}`}>
          <p>
            {popupMessageData[0]?.message ||
              (typeof popupMessageData[0] === 'string' ? popupMessageData[0] : 'Lead Transferred sucessfully')}
          </p>
        </div>
      )}

      {viewMode === 'dragAndDrop' && (
        <>


          <div className="boxes-wrapper">
            {/* Leads Box */}
            <div
              onDrop={onDropLeads}
              onDragOver={onDragOver}
              className="leads-box"
            >
              <h3>Available Leads</h3>
              {leadsDragDrop.length === 0 && <p className="placeholder-text">No available leads.</p>}
              {leadsDragDrop.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => onDragStart(lead)}
                  className="lead-tile"
                  title={`${lead.CompanyName || "N/A"} - ${lead.ContactPerson || "N/A"}`}
                >
                  {renderLeadDetails(lead)}
                </div>
              ))}
            </div>

            {/* Assigned Leads Box */}
            <div
              onDrop={onDropAssigned}
              onDragOver={onDragOver}
              className="assigned-box"
            >
              <h3>
                Assigned Leads to <select
                  value={selectedEmployeeDragDrop ? selectedEmployeeDragDrop.user_id : ""}
                  onChange={handleEmployeeChangeDragDrop}
                  className="employee-dropdowns"
                >
                  <option value="" disabled>
                    Select Employee
                  </option>
                  {employees.map((emp) => (
                    <option key={emp.user_id} value={emp.user_id}>
                      {emp.Name}
                    </option>
                  ))}
                </select>
              </h3>
              {assignedLeadsDragDrop.length === 0 && <p className="placeholder-text">Drag leads here to assign.</p>}
              {assignedLeadsDragDrop.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => onDragStart(lead)}
                  className="lead-tile assigned"
                  title={`${lead.CompanyName || "N/A"} - ${lead.ContactPerson || "N/A"}`}
                >
                  {renderLeadDetails(lead)}
                </div>
              ))}
            </div>
          </div>

          {/* Done Button */}
          {selectedEmployeeDragDrop && (
            <div className="save-button-container">
              <button
                onClick={handleSaveAssignmentsDragDrop}
                className="save-assignments-btn"
              >
                Done
              </button>
            </div>
          )}
        </>
      )}

      {viewMode === 'assign' && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
            {selectedLeadIndex !== null && (
              <button
                onClick={handleUpdateAssign}
                style={{
                  padding: "8px 14px",
                  backgroundColor: "#0a4f88",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  fontSize: "14px",
                  transition: "background-color 0.3s ease",
                }}
              >
                Assign To
              </button>
            )}
            <select
              value={selectedEmployeeAssign}
              onChange={handleEmployeeChangeAssign}
              className="styled-dropdown"
            >
              <option value="" disabled>
                Select Employee
              </option>
              {employees.map((emp) => (
                <option key={emp.user_id} value={emp.user_id}>
                  {emp.Name}
                </option>
              ))}
            </select>
          </div>

          <div
            className="tiles-container"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              opacity: selectedEmployeeAssign ? 1 : 0.5,
              pointerEvents: selectedEmployeeAssign ? "auto" : "none",
            }}
          >
            {leadsAssign.length === 0 && <p className="placeholder-text">No leads available.</p>}
            {leadsAssign.map((lead, index) => {
              const isSelected =
                firstSelectedIndex !== null &&
                lastSelectedIndex !== null &&
                index >= Math.min(firstSelectedIndex, lastSelectedIndex) &&
                index <= Math.max(firstSelectedIndex, lastSelectedIndex);

              return (
                <div
                  key={lead.id}
                  className={`tile ${isSelected ? "selected" : ""}`}
                  onClick={() => handleTileClick(index)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="icon-label">
                    <FaBuilding className="icon" />
                    <span className="truncate" title={lead['Company Name'] || "N/A"}>
                      {lead['Company Name'] || "N/A"}
                    </span>
                  </div>
                  <div className="icon-label">
                    <FaUser className="icon" /> {lead['Contact Person'] || "N/A"}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}